import type { AdminMember } from '@/api/queries/admin';
import { IconButton, StatusPill, Table, type TableColumn } from '@/components/ui';
import { formatDate } from '@/utils/formatDate';

interface MembersTableProps {
  members: AdminMember[];
  loading?: boolean;
  onDeactivate?: (member: AdminMember) => void;
  onRemove?: (member: AdminMember) => void;
}

function memberEmail(m: AdminMember): string {
  return m.invitedEmail ?? '—';
}

function statusTone(status: string): 'active' | 'inactive' | 'dnc' {
  if (status === 'active') return 'active';
  if (status === 'disabled') return 'dnc';
  return 'inactive';
}

function statusLabel(status: string): string {
  if (status === 'active') return 'Active';
  if (status === 'invited') return 'Invited';
  if (status === 'disabled') return 'Disabled';
  return status;
}

export function MembersTable({
  members,
  loading,
  onDeactivate,
  onRemove,
}: MembersTableProps) {
  const columns: TableColumn<AdminMember>[] = [
    {
      id: 'email',
      header: 'Email',
      cell: (m) => <span style={{ fontWeight: 500 }}>{memberEmail(m)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (m) => <StatusPill status={statusTone(m.status)}>{statusLabel(m.status)}</StatusPill>,
      width: 130,
    },
    {
      id: 'invitedAt',
      header: 'Invited',
      cell: (m) => formatDate(m.invitedAt) ?? '—',
      width: 130,
    },
    {
      id: 'joinedAt',
      header: 'Joined',
      cell: (m) => formatDate(m.joinedAt) ?? '—',
      width: 130,
    },
  ];

  return (
    <Table
      columns={columns}
      rows={members}
      rowKey={(m) => m.id}
      loading={loading}
      emptyMessage="No members yet."
      rowActions={
        onDeactivate || onRemove
          ? (m) => (
              <>
                {onDeactivate && m.status !== 'disabled' && (
                  <IconButton
                    icon="x"
                    size="sm"
                    label="Deactivate"
                    onClick={() => onDeactivate(m)}
                  />
                )}
                {onRemove && (
                  <IconButton
                    icon="trash"
                    size="sm"
                    variant="danger"
                    label="Remove"
                    onClick={() => onRemove(m)}
                  />
                )}
              </>
            )
          : undefined
      }
    />
  );
}
